import { useState } from "react";
import moment from "moment";
import { ChevronDownIcon, ChevronUpIcon } from "@heroicons/react/20/solid";

import SessionBonusPanel from "sessions/SessionBonusPanel";
import SessionDivider from "sessions/SessionDivider";
import { classNames } from "utility/Utils";
import { getCoachingPlanWeekBadgeImagePath } from "planEditor/BadgeUtils";
import { scoreBadgeBackgroundColor, scoreBarColor, scoreEncouragement, scoreTextColor, scoreTooltip } from "planEditor/ScoreUtils";

const SessionRoutinePanel = ({ sessionData, date, week }) => {

    const [opened, setOpened] = useState(false);

    const cleanLabel = (label: string) => {
        let limit: number = 20;

        if (label.length <= limit) {
            return (
                <div className="text-gray-700 text-sm font-medium leading-5">{label}</div>
            );
        }
        else {
            return (
                <div className="text-gray-700 text-sm font-medium relative has-tooltip leading-5 flex"><span className="tooltip rounded shadow-lg p-1 ml-10 mt-4">{label}</span>
                    <div className="hs-tooltip">{label.substring(0, limit) + "..."}</div>
                </div>
            );
        }
    };

    const timeConvert = (raw) => {
        let times = raw.split(":");
        let hours = times[0];
        let minutes = times[1];
        let AmOrPm = hours >= 12 ? "pm" : "am";
        hours = (hours % 12) || 12;
        let extraSpace = hours.length === 1 ? " " : "";
        return extraSpace + hours + ":" + minutes + AmOrPm;
    };

    const weeklyBestDecorator = () => {
        return (
            <div className="mix-blend-multiply justify-start items-start inline-flex">
                <div className="pl-1.5 pr-2 py-0.5 bg-brand-50 rounded-2xl justify-center items-center gap-1 flex">
                    <img className="w-3 h-3 relative" src="/icons/award-blue.svg" alt="icon"/>
                    <div className="text-center text-brand-700 text-xs font-medium leading-[18px]">
                        Weekly Best
                    </div>
                </div>
            </div>
        );
    };

    const allTimeBestDecorator = () => {
        return (
            <div className="mix-blend-multiply justify-start items-start inline-flex">
                <div className="pl-1.5 pr-2 py-0.5 bg-brand-50 rounded-2xl justify-center items-center gap-1 flex">
                    <img className="w-3 h-3 relative" src="/icons/award-blue.svg" alt="icon"/>
                    <div className="text-center text-brand-700 text-xs font-medium leading-[18px]">
                        All-Time Best
                    </div>
                </div>
            </div>
        );
    };

    const renderKarunaRatingRow = (rating) => {
        if (rating !== null) {
            return (
                <div className="w-full  justify-start items-start gap-2 inline-flex">
                    <span className="justify-start items-start gap-2 inline-flex [@media(max-width:1200px)]:w-[25%] w-[20%] [@media(min-width:1600px)]:w-[15%]  ">
                        <img className="w-5 h-5 relative flex-col justify-start items-start flex" src="/icons/icon.png" alt="icon"/>
                        {cleanLabel("Karuna Rating")}
                    </span>
                    <div className="grow shrink mt-1.5 relative">
                        <div className="w-[100%] absolute h-2 bg-gray-200 rounded-full"/>
                        <div className={classNames("h-2 absolute rounded-full", scoreBarColor(rating.score))} style={{ width: (Math.round((rating.score / 5000) * 100)) + "%" }}/>
                    </div>
                    <span className="inline-flex gap-2 text-sm">
                        {rating.score}
                        <div className={`px-2 py-0.5 has-tooltip relative rounded-2xl justify-center items-center flex ${scoreBadgeBackgroundColor(rating.score)}`}>
                            <span className="tooltip rounded shadow-lg p-1 -mt-12">{scoreTooltip(rating.score)}</span>
                            <div className={`text-center hs-tooltip text-xs font-medium leading-[18px] text-nowrap ${scoreTextColor(rating.score)}`}>{scoreEncouragement(rating.score)}</div>
                        </div>
                        <span>{rating.top_alltime && allTimeBestDecorator()}</span>
                        <span>{rating.top_weekly && weeklyBestDecorator()}</span>
                    </span>
                </div>
            );
        }
        else {
            return (
                <div className="w-full justify-start items-start gap-2 inline-flex">
                    <span className="justify-start items-start gap-2 inline-flex [@media(max-width:1200px)]:w-[25%] w-[20%] [@media(min-width:1600px)]:w-[15%]">
                        <img className="w-5 h-5 relative flex-col justify-start items-start flex" src="/icons/icon.png" alt="icon"/>
                        {cleanLabel("Karuna Rating")}
                    </span>
                    <div className="grow shrink mt-1.5 relative">
                        <div className="w-[100%] absolute h-2 bg-gray-200 rounded-full"/>
                    </div>
                    <span className="inline-flex gap-2 text-sm">
                        None
                    </span>
                </div>
            );
        }
    };

    const renderCompleted = (completed) => {
        if (completed) {
            return (
                <div className="mix-blend-multiply justify-start items-start flex">
                    <div className="pl-1.5 pr-2 py-0.5 bg-success-50 rounded-2xl justify-center items-center gap-1.5 flex">
                        <div className="w-2 h-2 relative">
                            <div className="w-1.5 h-1.5 left-[1px] top-[1px] absolute bg-success-500 rounded-full"/>
                        </div>
                        <div className="text-center text-success-700 text-xs font-medium leading-[18px]">Completed</div>
                    </div>
                </div>
            );
        }
        else {
            if (sessionData.progress_snapshot !== undefined) {
                let hasProgress = false;
                Object.values(sessionData.progress_snapshot).forEach(e => {
                    if (Object.values(e)[0] === 1) {hasProgress = true;}
                });
                if (hasProgress) {
                    return (
                        <div className="mix-blend-multiply justify-start items-start flex">
                            <div className="pl-1.5 pr-2 py-0.5 bg-lime-50 rounded-2xl justify-center items-center gap-1.5 flex">
                                <div className="w-2 h-2 relative">
                                    <div className="w-1.5 h-1.5 left-[1px] top-[1px] absolute bg-lime-400 rounded-full"/>
                                </div>
                                <div className="text-center text-lime-900 text-xs font-medium leading-[18px]">Partially Completed</div>
                            </div>
                        </div>
                    );
                }
                else {
                    return (
                        <div className="mix-blend-multiply justify-start items-start flex">
                            <div className="pl-1.5 pr-2 py-0.5 bg-warning-50 rounded-2xl justify-center items-center gap-1.5 flex">
                                <div className="w-2 h-2 relative">
                                    <div className="w-1.5 h-1.5 left-[1px] top-[1px] absolute bg-warning-500 rounded-full"/>
                                </div>
                                <div className="text-center text-warning-700 text-xs font-medium leading-[18px]">Incomplete</div>
                            </div>
                        </div>
                    );
                }
            }
            else {
                return (
                    <div className="mix-blend-multiply justify-start items-start flex">
                        <div className="pl-1.5 pr-2 py-0.5 bg-warning-50 rounded-2xl justify-center items-center gap-1.5 flex">
                            <div className="w-2 h-2 relative">
                                <div className="w-1.5 h-1.5 left-[1px] top-[1px] absolute bg-warning-500 rounded-full"/>
                            </div>
                            <div className="text-center text-warning-700 text-xs font-medium leading-[18px]">Incomplete</div>
                        </div>
                    </div>
                );
            }
        }
    };

    const snapShotWarning = () => {
        return (
            <div className="w-full pb-5 justify-start items-start gap-3 inline-flex">
                <img className="w-5 h-5 relative flex-col justify-start items-start flex" src="/icons/warning_icon.svg" alt="icon"/>
                <div className="text-amber-700 text-sm font-normal pt-0.5 leading-tight">
                    Sessions prior to 10th June 2024 cannot display complete snapshots of the routine when it was only partially completed.
                </div>
            </div>
        );
    };

    const renderActivities = () => {
        let activities = [];
        if (sessionData?.activities) {
            Object.keys(sessionData.activities).forEach((a, index) => {
                let act = sessionData.activities[a];
                if (act.instances) {
                    for (let i = 0; i < act.instances.length; i++) {
                        act.instances[i].id = act.id;
                        act.instances[i].name = act.name;
                        act.instances[i].type = act.type;
                        activities.push(
                            <SessionBonusPanel
                                key={`session-bonus-panel-${index}-${i}`}
                                activity={act.instances[i]}
                                routine={true}
                            />
                        );
                    }
                }
                else {
                    activities.push(
                        <SessionBonusPanel
                            key={`session-bonus-panel-no-activity-instances`}
                            activity={act}
                            routine={true}
                        />
                    );
                }
                if (index !== sessionData.activities.length - 1) {
                    activities.push(<SessionDivider key={`session-bonus-panel-divider-${index}`} label={""} activityDivider={true}/>);
                }
            });
        }
        return activities;
    };

    return (
        <div className="w-full flex justify-start items-center gap-5">
            <div className=" self-stretch flex-col justify-start items-end gap-2.5 inline-flex">
                <div className="text-right text-gray-500 text-sm font-medium leading-tight">{sessionData.start_time != null ? timeConvert(sessionData.start_time) : "n/a"}</div>
                <div className="w-px grow shrink basis-0 bg-gray-400"/>
                <div className="text-right text-gray-500 text-sm font-medium leading-tight">{sessionData.end_time != null ? timeConvert(sessionData.end_time) : "n/a"}</div>
            </div>
            {
                <div className="grow px-6 py-[15px] rounded-[10px] shadow border border-gray-200 flex-col justify-start items-start gap-[15px] flex cursor-pointer" onClick={() => setOpened(prev => !prev)}>
                    <div className="w-full flex flex-col gap-[15px]">
                        <div className="self-stretch justify-start items-center gap-[15px] inline-flex">
                            <div className="text-gray-900 text-xl font-semibold leading-[30px]">Personal Routine</div>
                            {
                                sessionData.iteration != null && sessionData.routine_completed
                                    ? (
                                        <div className="has-tooltip relative inline-flex">
                                            <span className="tooltip rounded shadow-lg ml-5 -mt-5">{`Completion ${sessionData.iteration}`}</span>
                                            <img className="w-7 h-7" src={getCoachingPlanWeekBadgeImagePath(sessionData.iteration, week)} alt="icon"/>
                                        </div>
                                    )
                                    : <img className="w-7 h-7" src={getCoachingPlanWeekBadgeImagePath()} alt="icon"/>
                            }
                            {renderCompleted(sessionData.routine_completed)}
                            <div className="grow shrink basis-0 h-6"/>
                            {
                                opened
                                    ? <ChevronUpIcon className="h-5 w-5 text-gray-700 relative" aria-hidden="true"/>
                                    : <ChevronDownIcon className="h-5 w-5 text-gray-700 relative" aria-hidden="true"/>
                            }
                        </div>
                        {sessionData.rating ? renderKarunaRatingRow(sessionData.rating) : renderKarunaRatingRow(null)}
                    </div>
                    {
                        opened && (
                            <div className="w-full self-stretch h-full flex flex-col items-stretch gap-2.5" onClick={(e) => e.stopPropagation()}>
                                <SessionDivider label={"Activities"} activityDivider={true}/>
                                {date.isBefore(moment("2024-06-10", "YYYY-MM-DD")) ? snapShotWarning() : <span></span>}
                                {renderActivities()}
                            </div>
                        )
                    }
                </div>
            }
        </div>
    );
};

export default SessionRoutinePanel;
